import { v4 } from "uuid";
import { OwcTooltip } from "@one/react";
import { getDateTimeDisplayValue, getDateTimeTooltipValue } from "../../utils/helpers/text";

/**
 * ag-grid cell renderer that displays date only but shows full datetime on hover via OwcTooltip.
 * Supports UTC datetime fields with fallback to legacy date/time fields.
 *
 * @param {object} props - ag-grid cell renderer props
 * @param {object} props.data - Row data
 * @param {string} props.utcDateTimeField - Field name for UTC datetime (e.g., 'runStartDateTime')
 * @param {string} props.legacyDateField - Field name for legacy date (e.g., 'runStartDate')
 * @param {string} props.legacyTimeField - Field name for legacy time (e.g., 'runStartTime')
 */
const DateTimeCellRenderer = ({ data, utcDateTimeField, legacyDateField, legacyTimeField }) => {
  const uniqueId = v4();
  const cellId = `datetime-cell-${uniqueId}`;

  const displayValue = getDateTimeDisplayValue(data, utcDateTimeField, legacyDateField);
  const tooltipValue = getDateTimeTooltipValue(data, utcDateTimeField, legacyDateField, legacyTimeField);

  return (
    <>
      <span id={cellId}>{displayValue}</span>
      <OwcTooltip anchor={cellId}>{tooltipValue}</OwcTooltip>
    </>
  );
};

/**
 * Factory function to create a DateTimeCellRenderer for specific fields.
 * Use this to create cell renderers for ag-grid column definitions.
 *
 * @param {string} utcDateTimeField - Field name for UTC datetime
 * @param {string} legacyDateField - Field name for legacy date
 * @param {string} legacyTimeField - Field name for legacy time
 * @returns {function} ag-grid cellRenderer function
 *
 * @example
 * // In column definition:
 * {
 *   field: 'runStartDate',
 *   cellRenderer: createDateTimeCellRenderer('runStartDateTime', 'runStartDate', 'runStartTime')
 * }
 */
export const createDateTimeCellRenderer = (utcDateTimeField, legacyDateField, legacyTimeField) => {
  return (params) => (
    <DateTimeCellRenderer
      data={params.data}
      utcDateTimeField={utcDateTimeField}
      legacyDateField={legacyDateField}
      legacyTimeField={legacyTimeField}
    />
  );
};

/**
 * Cell renderer for the "All Logs" view that handles both run logs and action logs.
 * Automatically determines the log type and uses appropriate datetime fields.
 *
 * @param {object} props - ag-grid cell renderer props
 * @param {object} props.data - Row data
 */
const AllLogDateCellRenderer = ({ data }) => {
  // Determine if this is a run log or action log
  const isRunLog = !!data?.runStartDate;

  // Select appropriate fields based on log type
  const utcDateTimeField = isRunLog ? 'runStartDateTime' : 'actionDateTime';
  const legacyDateField = isRunLog ? 'runStartDate' : 'actionDate';
  const legacyTimeField = isRunLog ? 'runStartTime' : 'actionTime';

  return (
    <DateTimeCellRenderer
      data={data}
      utcDateTimeField={utcDateTimeField}
      legacyDateField={legacyDateField}
      legacyTimeField={legacyTimeField}
    />
  );
};

/**
 * Cell renderer for "All Logs" view that auto-detects run log vs action log.
 * @returns {function} ag-grid cellRenderer function
 */
export const createAllLogDateCellRenderer = () => {
  return (params) => <AllLogDateCellRenderer data={params.data} />;
};

export default DateTimeCellRenderer;
